import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  fetchClasses,
  fetchRegistrationsForUser,
} from "../api/backend";
import {
  Box, Typography, Button,
} from "@mui/material";
import { useTenant } from "../contexts/TenantContext";
import { Link, useNavigate } from "react-router-dom";
import {
  getCourseCatalogTokens,
  getCategoryMeta,
} from "./courseCatalog/courseCatalogDesign";

const MONTH_NAMES = ["","January","February","March","April","May","June","July","August","September","October","November","December"];

function getCatalogPresentation(theme) {
  const tokens = getCourseCatalogTokens(theme);
  const { palette } = theme;
  return {
    ...tokens,
    ink: palette.text.primary,
    ink2: palette.text.secondary,
    ink3: alpha(palette.text.primary, 0.62),
    ink4: alpha(palette.text.primary, 0.42),
    line: palette.divider,
    // The supplied catalog mock uses its quiet hairline for every internal
    // separator.  Do not derive these from the tenant primary color.
    line2: palette.divider,
    lineStrong: alpha(palette.text.primary, 0.25),
    surface: tokens.paper,
    surface2: palette.action.hover,
    surface3: palette.action.selected,
    danger: palette.error.main,
    danger50: alpha(palette.error.main, 0.08),
    danger100: alpha(palette.error.main, 0.18),
    grey: palette.text.secondary,
    greyBg: alpha(palette.text.secondary, 0.10),
    shadowSm: theme.shadows[1],
    shadowMd: theme.shadows[4],
  };
}

function getAvMeta(secondary, accent, colors) {
  return {
    open: { label: "Seats available", color: secondary, bg: alpha(secondary, 0.10), border: alpha(secondary, 0.20) },
    low:  { label: "Filling up",      color: accent,    bg: alpha(accent, 0.10),    border: alpha(accent, 0.22)   },
    full: { label: "Full",            color: colors.danger, bg: colors.danger50, border: colors.danger100 },
    wait: { label: "Waitlist open",   color: colors.grey, bg: colors.greyBg, border: alpha(colors.grey, 0.20) },
    unknown: { label: "Availability TBD", color: colors.grey, bg: colors.greyBg, border: alpha(colors.grey, 0.20) },
  };
}

function getAvState(cls) {
  const availableSeats = cls.availableSeats == null ? NaN : Number(cls.availableSeats);
  if (!Number.isFinite(availableSeats)) return "unknown";
  if (availableSeats === 0) return cls.waitlistAvailable ? "wait" : "full";
  if (availableSeats <= 3) return "low";
  return "open";
}

function getFirstClassDate(cls) {
  if (Array.isArray(cls?.schedule) && cls.schedule.length > 0) return cls.schedule[0].start;
  return cls?.date || null;
}

function formatClassDate(cls) {
  const d = getFirstClassDate(cls);
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function getClassMonth(cls) {
  const d = getFirstClassDate(cls);
  if (!d) return null;
  return new Date(d).getMonth() + 1;
}

function hasPrereq(cls) {
  const p = cls.prereqs;
  return p && p.trim() !== "" && p.trim().toLowerCase() !== "none";
}

function getDeliveryFormat(cls) {
  return cls.deliveryMethod || cls.deliveryFormat || cls.format || "Instructor-Led";
}

const DELIVERY_METHODS = [
  { value: "instructor", label: "Instructor-Led" },
  { value: "online", label: "Online / Self-Paced" },
  { value: "hybrid", label: "Hybrid" },
];

const AVAILABILITY_OPTIONS = [
  { value: "open", label: "Open Seats" },
  { value: "low", label: "Low Seats (≤ 3)" },
  { value: "full", label: "Full" },
  { value: "wait", label: "Waitlist Available" },
];

function getDeliveryMethod(cls) {
  const format = getDeliveryFormat(cls);
  if (!format || typeof format !== "string") return "instructor";
  const normalized = format.trim().toLowerCase();
  if (normalized.includes("hybrid")) return "hybrid";
  if (normalized.includes("online") || normalized.includes("self-paced") || normalized.includes("self paced")) return "online";
  if (normalized.includes("instructor")) return "instructor";
  return "instructor";
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function Svg({ d, size = 13, style, ...props }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} aria-hidden="true" {...props}>
      {props.children}
    </svg>
  );
}

const IcoCheck  = ({ size = 13 }) => <Svg size={size}><polyline points="20 6 9 17 4 12"/></Svg>;
const IcoWarn   = ({ size = 13 }) => <Svg size={size}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></Svg>;
const IcoBook   = () => <Svg size={36}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></Svg>;
const IcoFilter = () => <Svg size={15}><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></Svg>;
const IcoSearch = () => <Svg size={16}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Svg>;
const IcoRight  = () => <Svg size={12}><polyline points="9 18 15 12 9 6"/></Svg>;

// ─── Button style presets ────────────────────────────────────────────────────
const BTN_BASE = {
  fontSize: "12.5px", fontWeight: 600, height: "34px", px: "13px",
  borderRadius: "9px", textTransform: "none", whiteSpace: "nowrap", minWidth: 0,
  lineHeight: 1,
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function Pill({ bg, border, color, dot, children }) {
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      fontSize: "10.5px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
      padding: "4px 9px", borderRadius: "999px",
      bgcolor: bg, color, border: `1px solid ${border}`,
      flexShrink: 0, whiteSpace: "nowrap",
    }}>
      {dot && <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor", flexShrink: 0 }} />}
      {children}
    </Box>
  );
}

// Small status tag shown above the seat count: "Filling up" pulses, "Full" is static.
function SeatTag({ avState, avMeta }) {
  if (avState !== "low" && avState !== "full" && avState !== "wait") return null;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
      px: "8px", py: "3px", borderRadius: "4px", bgcolor: avMeta.bg, color: avMeta.color,
    }}>
      {avState === "low" && (
        <Box component="span" sx={{
          width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor",
          animation: "catalogSeatPulse 1.6s ease-in-out infinite",
          "@keyframes catalogSeatPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }} />
      )}
      {avMeta.label}
    </Box>
  );
}

// Right-hand seat summary: big remaining count, fill bar, "x of y taken".
function SeatSummary({ cls, avState }) {
  const theme = useTheme();
  const colors = getCatalogPresentation(theme);
  const avMeta = getAvMeta(colors.secondary, theme.palette.warning.main, colors)[avState];
  const availableSeats = cls.availableSeats == null ? NaN : Number(cls.availableSeats);

  if (!Number.isFinite(availableSeats)) {
    return null;
  }

  const rawTotal = Number(cls.capacity || cls.maxStudents);
  const total = Number.isFinite(rawTotal) && rawTotal > 0 ? rawTotal : null;
  const taken = total ? Math.min(total, Math.max(0, total - availableSeats)) : 0;
  const numberColor = avState === "full" ? colors.danger : avState === "low" ? avMeta.color : colors.ink;
  const barColor = avState === "full" ? colors.danger : avState === "low" ? avMeta.color : colors.secondary;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", sm: "flex-end" }, gap: "6px", whiteSpace: "nowrap" }}>
      <SeatTag avState={avState} avMeta={avMeta} />
      <Box component="span" sx={{ fontSize: "13.5px", color: colors.ink3 }}>
        <Box component="b" sx={{
          fontFamily: "var(--font-display)", fontSize: "22px", fontWeight: 600, color: numberColor,
          mr: "4px", verticalAlign: "-2px", fontVariantNumeric: "tabular-nums",
        }}>
          {availableSeats}
        </Box>
        {" "}{availableSeats === 1 ? "seat" : "seats"} remaining
      </Box>
      {total && (
        <>
          <Box sx={{ width: 140, height: 6, borderRadius: "3px", bgcolor: colors.surface3, overflow: "hidden" }}>
            <Box
              role="progressbar"
              aria-label={`${taken} of ${total} seats filled`}
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={taken}
              sx={{ height: "100%", width: `${Math.round((taken / total) * 100)}%`, borderRadius: "3px", bgcolor: barColor }}
            />
          </Box>
          <Box component="span" sx={{ fontSize: "12px", color: colors.ink3, fontVariantNumeric: "tabular-nums" }}>
            {taken} of {total} taken
          </Box>
        </>
      )}
    </Box>
  );
}

function CourseRow({ course: c, isRegistered, isWaitlisted, isTeaching, navigate }) {
  const theme = useTheme();
  const colors = getCatalogPresentation(theme);
  const tPrimary = getCourseCatalogTokens(theme).primary;
  const tAccent = theme.palette.warning.main;
  const catMeta = getCategoryMeta(c.category, theme);
  const avState = getAvState(c);
  const avMeta = getAvMeta(colors.secondary, tAccent, colors)[avState];
  const dateStr = formatClassDate(c);
  const extraSessions = Array.isArray(c.schedule) && c.schedule.length > 1 ? c.schedule.length - 1 : 0;
  const myStatus = isTeaching ? "Teaching" : isWaitlisted ? "Waitlisted" : isRegistered ? "Enrolled" : null;
  const go = () => navigate("/classes/" + c.id);
  const sep = <Box component="span" aria-hidden="true">·</Box>;

  return (
    <Box component="article" aria-labelledby={`course-${c.id}-title`} onClick={go} sx={{
      display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) auto" },
      gap: "20px", alignItems: "center", p: "18px 20px", cursor: "pointer",
      borderTop: `1px solid ${colors.line2}`, "&:first-of-type": { borderTop: 0 },
      transition: "background .12s", "&:hover": { bgcolor: colors.surface2 },
    }}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <Pill bg={catMeta.bg} border={catMeta.border} color={catMeta.color} dot>{c.category || "General"}</Pill>
          {c.courseNumber && (
            <Box component="span" sx={{ fontFamily: "var(--font-mono, 'JetBrains Mono','Courier New',monospace)", fontSize: "12px", color: colors.ink3 }}>
              {c.courseNumber}
            </Box>
          )}
          {avState === "unknown" && <Pill bg={avMeta.bg} border={avMeta.border} color={avMeta.color}>{avMeta.label}</Pill>}
          {myStatus && (
            <Pill
              bg={isWaitlisted ? colors.greyBg : alpha(tPrimary, 0.06)}
              border={isWaitlisted ? alpha(colors.grey, 0.20) : alpha(tPrimary, 0.18)}
              color={isWaitlisted ? colors.grey : tPrimary}
            >
              {!isWaitlisted && <IcoCheck size={9} />}
              {myStatus}
            </Pill>
          )}
        </Box>

        <Typography component="h2" id={`course-${c.id}-title`} sx={{
          fontFamily: theme.typography.h1?.fontFamily || theme.typography.fontFamily,
          fontSize: "17px", fontWeight: 600, letterSpacing: "-.01em", lineHeight: 1.3,
          color: colors.ink, m: 0, mt: "8px",
        }}>
          <Box
            component={Link}
            to={`/classes/${c.id}`}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: "inherit", textDecoration: "none", borderRadius: "4px",
              "&:hover": { textDecoration: "underline" },
              "&:focus-visible": { outline: `3px solid ${alpha(tPrimary, 0.24)}`, outlineOffset: "3px" },
            }}
          >
            {c.title}
          </Box>
        </Typography>

        {c.description && (
          <Typography sx={{
            fontSize: "13.5px", color: colors.ink2, m: 0, mt: "4px", lineHeight: 1.45,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {c.description}
          </Typography>
        )}

        <Box data-testid={`course-metadata-${c.id}`} sx={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px",
          mt: "10px", fontSize: "13px", color: colors.ink3,
        }}>
          {dateStr && <><span>Starts {dateStr}</span>{sep}</>}
          {extraSessions > 0 && <><span>+{extraSessions} more {extraSessions === 1 ? "session" : "sessions"}</span>{sep}</>}
          <span>{getDeliveryFormat(c)}</span>
          {hasPrereq(c) && (
            <>
              {sep}
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px", color: tAccent, fontWeight: 600 }}>
                <IcoWarn size={12} />Prereq: {c.prereqs}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{
        display: "flex", flexDirection: { xs: "row", sm: "column" },
        alignItems: { xs: "center", sm: "flex-end" }, justifyContent: "space-between", gap: "10px",
      }}>
        <SeatSummary cls={c} avState={avState} />
        <Button
          onClick={(e) => { e.stopPropagation(); go(); }}
          endIcon={<IcoRight />}
          sx={{
            ...BTN_BASE, bgcolor: colors.surface, color: colors.ink, border: `1px solid ${colors.line}`,
            "&:hover": { bgcolor: colors.surface2, borderColor: colors.lineStrong },
          }}
        >
          Details
        </Button>
      </Box>
    </Box>
  );
}

// One always-open facet: uppercase legend + native checkboxes with counts.
function Facet({ title, options, selected, onToggle }) {
  const theme = useTheme();
  const colors = getCatalogPresentation(theme);
  const tPrimary = theme.palette.primary.main;
  if (options.length === 0) return null;
  return (
    <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
      <Box component="legend" sx={{ p: 0, mb: "8px", fontSize: "10px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: colors.ink3 }}>
        {title}
      </Box>
      {options.map(({ value, label, count }) => (
        <Box component="label" key={value} sx={{
          display: "grid", gridTemplateColumns: "18px minmax(0, 1fr) auto", gap: "10px", alignItems: "center",
          px: "8px", py: "6px", borderRadius: "6px", cursor: "pointer", fontSize: "14px", color: colors.ink2,
          "&:hover": { bgcolor: colors.surface3 },
        }}>
          <Box component="input" type="checkbox" checked={selected.includes(value)} onChange={() => onToggle(value)}
            sx={{ m: 0, width: 16, height: 16, accentColor: tPrimary, cursor: "pointer" }} />
          <span>{label}</span>
          <Box component="span" aria-hidden="true" sx={{ fontSize: "11.5px", color: colors.ink4, fontVariantNumeric: "tabular-nums" }}>{count}</Box>
        </Box>
      ))}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ClassList() {
  const theme = useTheme();
  const colors = getCatalogPresentation(theme);
  const tPrimary = getCourseCatalogTokens(theme).primary;
  const BTN_OUTLINE = {
    ...BTN_BASE, bgcolor: colors.surface, color: colors.ink,
    border: `1px solid ${colors.line}`,
    "&:hover": { bgcolor: colors.surface2, borderColor: colors.lineStrong },
  };
  const [classes, setClasses]               = useState([]);
  const [filter, setFilter]                 = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedAv, setSelectedAv]         = useState([]);
  const [selectedDeliveryMethods, setSelectedDeliveryMethods] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [sortBy, setSortBy]                 = useState("date");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const { user, isFeatureEnabled } = useTenant();
  const navigate = useNavigate();

  const [userRegistrations, setUserRegistrations] = useState([]);

  const loadClasses = useCallback(() => {
    if (!user?.tenantId) return;
    fetchClasses(user.tenantId, null, true).then((response) => {
      setClasses(Array.isArray(response) ? response : (response.data || []));
    });
  }, [user]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  useEffect(() => {
    if (!user?.tenantId) return;
    fetchRegistrationsForUser(user.id, user.tenantId).then((regs) => {
      setUserRegistrations(Array.isArray(regs) ? regs : []);
    });
  }, [user]);

  const enrolledClassIds = useMemo(() =>
    new Set((userRegistrations || []).filter(r => r?.enrollmentStatus === "ENROLLED").map(r => r.classId)),
    [userRegistrations]
  );
  const waitlistedClassIds = useMemo(() =>
    new Set((userRegistrations || []).filter(r => r?.enrollmentStatus === "WAITLISTED").map(r => r.classId)),
    [userRegistrations]
  );

  const categories = useMemo(() => [...new Set(classes.map(c => c.category).filter(Boolean))], [classes]);

  const availableMonths = useMemo(() => {
    const ms = new Set();
    classes.forEach(c => { const m = getClassMonth(c); if (m) ms.add(m); });
    return [...ms].sort((a, b) => a - b);
  }, [classes]);

  const filteredClasses = useMemo(() => {
    let list = [...classes];
    if (selectedCategories.length) list = list.filter(c => selectedCategories.includes(c.category));
    if (selectedAv.length)         list = list.filter(c => selectedAv.includes(getAvState(c)));
    if (selectedDeliveryMethods.length) list = list.filter(c => selectedDeliveryMethods.includes(getDeliveryMethod(c)));
    if (selectedMonths.length)     list = list.filter(c => selectedMonths.includes(getClassMonth(c)));
    if (filter) {
      const q = filter.toLowerCase();
      list = list.filter(c =>
        [c.title, c.courseNumber, c.category, c.description].filter(Boolean).join(" ").toLowerCase().includes(q)
      );
    }
    if (sortBy === "name")  list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    else if (sortBy === "seats") list.sort((a, b) => (b.availableSeats || 0) - (a.availableSeats || 0));
    else list.sort((a, b) => new Date(getFirstClassDate(a) || 0) - new Date(getFirstClassDate(b) || 0));
    return list;
  }, [classes, selectedCategories, selectedAv, selectedDeliveryMethods, selectedMonths, filter, sortBy]);

  const toggleIn = (setter) => (v) => setter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]);
  const activeCount = selectedCategories.length + selectedAv.length + selectedDeliveryMethods.length + selectedMonths.length;
  const clearAll = () => {
    setSelectedCategories([]); setSelectedAv([]); setSelectedDeliveryMethods([]); setSelectedMonths([]); setFilter("");
  };

  if (!isFeatureEnabled("classes")) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>Class Listing Unavailable</Typography>
        <Typography color="text.secondary">Class registration is not enabled for your training center.</Typography>
      </Box>
    );
  }

  const countBy = (fn, v) => classes.filter(c => fn(c) === v).length;

  const sidebarContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <Facet title="Availability" selected={selectedAv} onToggle={toggleIn(setSelectedAv)}
        options={AVAILABILITY_OPTIONS.map(o => ({ ...o, count: countBy(getAvState, o.value) }))} />
      <Facet title="Category" selected={selectedCategories} onToggle={toggleIn(setSelectedCategories)}
        options={categories.map(cat => ({ value: cat, label: cat, count: countBy(c => c.category, cat) }))} />
      <Facet title="Delivery Method" selected={selectedDeliveryMethods} onToggle={toggleIn(setSelectedDeliveryMethods)}
        options={DELIVERY_METHODS.map(m => ({ value: m.value, label: m.label, count: countBy(getDeliveryMethod, m.value) }))} />
      <Facet title="Start Month" selected={selectedMonths} onToggle={toggleIn(setSelectedMonths)}
        options={availableMonths.map(m => ({ value: m, label: MONTH_NAMES[m], count: countBy(getClassMonth, m) }))} />
      {activeCount > 0 && (
        <Box component="button" type="button" onClick={clearAll} sx={{
          alignSelf: "flex-start", fontFamily: "inherit", fontSize: "13px", fontWeight: 600, color: tPrimary,
          bgcolor: "transparent", border: 0, p: "4px 8px", ml: "-8px", borderRadius: "6px", cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}>
          Clear filters
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1380, mx: "auto", boxSizing: "border-box", px: { xs: "16px", sm: "40px" }, pt: { xs: "18px", sm: "28px" }, pb: { xs: "80px", sm: "100px" } }}>
      {/* page header */}
      <Box sx={{ mb: "24px" }}>
        <Typography component="h1" sx={{ m: 0, fontSize: { xs: "28px", sm: "32px" }, fontWeight: 600, letterSpacing: "-.01em", lineHeight: 1.1, color: colors.ink, fontFamily: "var(--font-display)" }}>
          Course Catalog
        </Typography>
        <Typography sx={{ m: 0, mt: "8px", fontSize: "14.5px", color: colors.ink2 }}>
          Browse upcoming training across all departments and enroll in open classes.
        </Typography>
      </Box>

      {/* search + sort */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "24px", flexWrap: "wrap" }}>
        <Box sx={{
          display: "flex", alignItems: "center", gap: "10px", height: "44px", px: "14px",
          bgcolor: colors.surface, border: `1px solid ${colors.line}`, borderRadius: "8px", flex: "1 1 360px", minWidth: "200px",
          "&:focus-within": { borderColor: tPrimary, boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.12)}` },
        }}>
          <Box sx={{ color: colors.ink4, display: "inline-flex" }}><IcoSearch /></Box>
          <Box component="input" type="search" role="searchbox" aria-label="Search courses" value={filter} onChange={e => setFilter(e.target.value)}
            placeholder="Search by name, course number or category"
            sx={{ border: 0, outline: 0, fontFamily: "inherit", fontSize: "14px", color: colors.ink, bgcolor: "transparent", width: "100%" }} />
        </Box>
        <Box component="select" aria-label="Sort courses" value={sortBy} onChange={e => setSortBy(e.target.value)} sx={{
          height: "44px", px: "13px", bgcolor: colors.surface, border: `1px solid ${colors.line}`,
          borderRadius: "8px", fontFamily: "inherit", fontSize: "14px", color: colors.ink2,
          cursor: "pointer", outline: 0, minWidth: "200px",
          "&:focus": { borderColor: tPrimary },
        }}>
          <option value="date">Sort: Start date</option>
          <option value="name">Sort: Name</option>
          <option value="seats">Sort: Most seats left</option>
        </Box>
      </Box>

      {/* mobile filter toggle */}
      <Box sx={{ display: "none", "@media (max-width:800px)": { display: "block" }, mb: 2 }}>
        <Button aria-label="Open course filters" aria-expanded={mobileFiltersOpen} onClick={() => setMobileFiltersOpen(open => !open)}
          sx={{ ...BTN_OUTLINE, width: "100%", justifyContent: "space-between", display: "inline-flex", gap: "7px" }}>
          <IcoFilter /> Filters{activeCount > 0 ? ` (${activeCount})` : ""}
        </Button>
        {mobileFiltersOpen && (
          <Box sx={{ mt: 1, p: "16px", bgcolor: colors.surface, border: `1px solid ${colors.line}`, borderRadius: "12px" }}>
            {sidebarContent}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "240px minmax(0, 1fr)", gap: "32px", alignItems: "start", "@media (max-width:800px)": { gridTemplateColumns: "minmax(0, 1fr)" } }}>
        <Box component="aside" aria-label="Course filters" sx={{ position: "sticky", top: "80px", "@media (max-width:800px)": { display: "none" } }}>
          {sidebarContent}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Box data-testid="course-count" sx={{ fontSize: "13px", color: colors.ink3, mb: "12px" }}>
            {filteredClasses.length} {filteredClasses.length === 1 ? "course" : "courses"}
          </Box>

          {filteredClasses.length === 0 ? (
            <Box sx={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: "8px", py: "56px", px: "20px", textAlign: "center",
              border: `1.5px dashed ${colors.lineStrong}`, borderRadius: "12px",
            }}>
              <Box sx={{ color: colors.ink4 }}><IcoBook /></Box>
              <Typography sx={{ m: 0, fontSize: "16px", fontWeight: 600, color: colors.ink }}>No courses found</Typography>
              <Typography sx={{ m: 0, fontSize: "14px", color: colors.ink2, maxWidth: "320px" }}>
                Try adjusting your filters or search query.
              </Typography>
              {(activeCount > 0 || filter) && (
                <Button onClick={clearAll} sx={{ ...BTN_OUTLINE, mt: "8px" }}>Clear filters</Button>
              )}
            </Box>
          ) : (
            <Box sx={{ bgcolor: colors.surface, border: `1px solid ${colors.line}`, borderRadius: "12px", overflow: "hidden", boxShadow: colors.shadowSm }}>
              {filteredClasses.map(c => (
                <CourseRow
                  key={c.id}
                  course={c}
                  isRegistered={enrolledClassIds.has(c.id)}
                  isWaitlisted={waitlistedClassIds.has(c.id)}
                  isTeaching={!!(user && user.id === c.instructorId)}
                  navigate={navigate}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
